import { getAccessToken, getRefreshToken, saveTokens, removeTokens } from './auth'

// Use Vite env variable or fallback to default
// In production, use relative path (same server), in development use localhost:8080
const API_BASE = import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.PROD ? '' : 'http://localhost:8080')

// Shared refresh promise: ensures only one /api/refresh call runs at a time.
// All concurrent 401 handlers wait for the same promise instead of each firing
// their own request, which would exhaust the auth rate limit (5/min).
let refreshPromise = null

function notifySessionExpired() {
  removeTokens()
  globalThis.dispatchEvent(new CustomEvent('auth:session-expired'))
}

async function doRefreshWithLocalStorage(refreshToken) {
  const refreshRes = await fetch(API_BASE + '/api/refresh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ refresh_token: refreshToken }),
  })

  if (!refreshRes.ok) {
    notifySessionExpired()
    return null
  }

  const data = await refreshRes.json()
  saveTokens(data.access_token, data.refresh_token)
  return data.access_token
}

async function doRefreshWithCookie() {
  const refreshRes = await fetch(API_BASE + '/api/refresh', {
    method: 'POST',
    credentials: 'include',
  })

  if (!refreshRes.ok) {
    notifySessionExpired()
    return null
  }

  return true
}

async function refreshWithLocalStorage(refreshToken, url, options, headers) {
  if (!refreshPromise) {
    refreshPromise = doRefreshWithLocalStorage(refreshToken).finally(() => {
      refreshPromise = null
    })
  }

  const newAccessToken = await refreshPromise
  if (!newAccessToken) return null

  headers['Authorization'] = `Bearer ${newAccessToken}`
  return await fetch(url, { ...options, headers, credentials: 'include' })
}

async function refreshWithCookie(url, options, headers) {
  if (!refreshPromise) {
    refreshPromise = doRefreshWithCookie().finally(() => {
      refreshPromise = null
    })
  }

  const ok = await refreshPromise
  if (!ok) return null

  // server updated cookies for access_token and refresh_token
  return await fetch(url, { ...options, headers, credentials: 'include' })
}

async function handleUnauthorized(accessToken, url, options, headers) {
  if (accessToken) {
    // non-cookie mode: existing localStorage refresh logic
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      notifySessionExpired()
      return null
    }
    return await refreshWithLocalStorage(refreshToken, url, options, headers)
  }

  // cookie-based mode: silent refresh via cookies
  return await refreshWithCookie(url, options, headers)
}

export async function apiFetch(path, options = {}) {
  const url = path.startsWith('http') ? path : API_BASE + path
  const accessToken = getAccessToken()

  // Don't set Content-Type for FormData - browser will set it automatically with boundary
  const headers = options.body instanceof FormData
    ? { ...options.headers }
    : { 'Content-Type': 'application/json', ...options.headers }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }

  let response = await fetch(url, { ...options, headers, credentials: 'include' })

  if (response.status === 401) {
    const refreshedResponse = await handleUnauthorized(accessToken, url, options, headers)
    return refreshedResponse || response
  }

  return response
} 